"use client"

// Bundle Performance view.
//
// Reads /api/integrations/shopify/bundle-performance — one row per
// `bundle_definitions` registry entry, with per-period and lifetime
// (baseline + current) metrics. The bundle's `starts_at` anchors the
// lifetime current+baseline window pair.

import { memo, useCallback, useMemo, useState } from "react"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Switch } from "@/components/ui/switch"
import { KpiCard, KpiCardSkeleton } from "@/components/ui/analytics/kpi-card"
import { AlertTriangle, ArrowUpRight, DollarSign, Info, Package, TrendingUp } from "lucide-react"
import { cn } from "@/lib/utils"
import { format, parse as parseDate } from "date-fns"
import { useBundlePerformance } from "./use-bundle-performance"
import {
  computeBundleTypePerformance,
  lifetimeIntersectsPeriod,
  type ComputedBundleTypePerformance,
} from "./economics-calculations"
import { BundleTypeDrillDown, type DrillDownTab } from "./bundle-type-drill-down"
import { ThHeader } from "./th-header"
import { useRecipeCogsWarning } from "./recipe-cogs-warning"
import { fmtCurrency, fmtInt, fmtPct } from "./formatters"
import { STICKY_NAME_COL, STICKY_NAME_COL_INNER_MAX } from "./sticky-col"

// `starts_at` / `ends_at` are Postgres `date` columns — already calendar
// literals (YYYY-MM-DD), no timezone conversion. We parse via date-fns
// (NOT `new Date(string)`, which interprets as UTC midnight and shifts a
// day in negative offsets) and format the local-date Date object.
function formatYmdLiteral(ymd: string | null): string {
  if (!ymd) return "—"
  try {
    return format(parseDate(ymd, "yyyy-MM-dd", new Date()), "MMM d, yyyy")
  } catch {
    return ymd
  }
}
import type { EconomicsSettings } from "./types"
import type { BundleBaselineStatus, BundleTypePerformance } from "@/lib/shopify/bundles/types"
import { useTranslations } from "next-intl"



// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Tooltip text per baseline_status. Status values mirror the
// `BundleBaselineStatus` enum exactly — extending the enum requires adding
// the matching case here (TS won't compile without it). User-facing term
// «start date» mirrors the Constructor's «Starts on» field label; the
// underlying column is `bundle_definitions.starts_at`.
const BASELINE_STATUS_TOOLTIP: Record<Exclude<BundleBaselineStatus, "ok">, string> = {
  no_metadata: "Set a start date on this bundle definition to see lifetime cannibalization metrics",
  too_young: "Bundle is too young (< 7 days) — lifetime sample too small for stable metrics",
  too_few_orders: "Fewer than 15 lifetime bundle orders — sample too small for stable cannibalization metrics",
  insufficient_history:
    "Not enough order history before bundle launch — lifetime baseline window can't be computed",
  no_data: "No qualifying orders in the lifetime baseline window",
}

// Bundle Share — share of family-component UNITS sold inside this bundle in
// the current period. Unit-based to match adjacent Bundle / Standalone unit
// columns in the table; revenue-based was confusing because price differences
// between bundle and solo SKUs broke the visual correspondence with units.
//
// This is the corrected metric that USED to be called "Cannibal. %" but was
// neither industry-standard cannibalization (which needs baseline) nor visually
// consistent with what users see.
const BUNDLE_SHARE_TOOLTIP =
  "Share of this family's units sold inside the bundle in the current period (bundle units ÷ total family units). " +
  "Higher = more demand captured by the bundle. " +
  "Does NOT measure cannibalization — see True Cannibalization for that (requires launch date)."

// Lifetime cannibalization — industry-standard metric, lifetime-scoped.
// Compares the bundle's last N days against the same N days BEFORE its
// start (N = bundle age, capped at 12 months). Independent of the UI
// period selector.
//
// Formula (server, computeTrueCannibalizationRate):
//   max(0, baseline_units − lifetime_standalone_units) / lifetime_bundle_units
// clamped to [0, 1].
const TRUE_CANNIBALIZATION_TOOLTIP =
  "Estimated share of bundle sales absorbed from previous standalone demand. " +
  "Compares the bundle's last N days against the same N days before its start (N = bundle age, capped at 12 months). " +
  "Lifetime-scoped — independent of the period selector above. " +
  "0% = bundle attracted only new customers; 100% = every bundle unit displaced a solo sale. " +
  "Available only when a start date is set and baseline data exists."

// Shared cap+symmetry phrasing used by every Group 2 column tooltip.
const LIFETIME_WINDOW_NOTE =
  " Lifetime window: bundle's last N days vs the same N days before launch (N = bundle age, capped at 12 months)."

const BASELINE_TRUNCATED_TOOLTIP_BASE =
  "Effective window shorter than 12 months — limited by the integration's pre-launch order history."

// Reasons to suppress Bundle Share with "—" rather than display a misleading
// extreme value. The metric is unit-based now, so suppression rules also use
// unit counts, not revenue.
const BUNDLE_SHARE_LOW_VOLUME = 10 // total family units (bundle + standalone)
const BUNDLE_SHARE_LOW_BUNDLE_ORDERS = 10

const BUNDLE_SHARE_LOW_TOOLTIP =
  "Too few units in the family pool (< 10) to compute a stable Bundle Share. Use drill-down to inspect SKU mix."

const BUNDLE_SHARE_FEW_ORDERS_TOOLTIP =
  "Few bundle orders (n < 10). Bundle Share is technically correct but easily skewed by small samples. Use drill-down to verify."

const STANDALONE_OVERLAP_NOTE =
  " A solo line_item is counted for every bundle type whose families it spans, " +
  "so the same standalone value can repeat across rows that share families. " +
  "Don't sum per-type standalone columns — see Bundle Revenue KPI for non-overlapping totals."

const INCREMENTAL_SEASONALITY_NOTE =
  " Negative values can reflect seasonality, marketing changes, or real demand drop, " +
  "not just bundle launch impact."

// Profit Lift coloring (lifetime-scoped):
//   positive → green; negative → red; |Δ| < 0.5% of lifetime_current revenue → neutral.
// Threshold scales with lifetime_current (NOT current_all) so a 12-month-old
// bundle with low UI-period activity still gets meaningful neutral-band sizing.
function profitLiftColorClass(profitLift: number, lifetimeCurrentRevenue: number): string {
  const threshold = Math.abs(lifetimeCurrentRevenue) * 0.005
  if (Math.abs(profitLift) < threshold) return "text-muted-foreground"
  return profitLift > 0 ? "text-green-600" : "text-red-600"
}

// `lifetimeIntersectsPeriod` lives in economics-calculations.ts now (pure
// logic, tz-aware). See its JSDoc for the boundary rules and the
// negative-offset gotcha.

/**
 * Compute Bundle Share as a unit-based ratio:
 *   bundle_units / (bundle_units + standalone_units)
 *
 * Returns suppression reason when the family unit pool is too small for the
 * metric to be stable, OR when bundle volume itself is so low that any ratio
 * is noise.
 */
function bundleShareDisplay(type: BundleTypePerformance, tUi: ReturnType<typeof useTranslations<"Shopify.ShopifyEconomicsBundlePerformanceView">>):
  | { ok: true; value: number }
  | { ok: false; tooltip: string } {
  const bundleUnits = type.bundle.units_sold
  const standaloneUnits = type.standalone.units_sold
  const totalFamilyUnits = bundleUnits + standaloneUnits

  if (totalFamilyUnits <= 0) {
    return { ok: false, tooltip: tUi("noFamilyUnitsSoldInThisPeriod") }
  }
  if (totalFamilyUnits < BUNDLE_SHARE_LOW_VOLUME) {
    return { ok: false, tooltip: BUNDLE_SHARE_LOW_TOOLTIP }
  }
  if (type.bundle.order_count < BUNDLE_SHARE_LOW_BUNDLE_ORDERS) {
    return { ok: false, tooltip: BUNDLE_SHARE_FEW_ORDERS_TOOLTIP }
  }
  return { ok: true, value: bundleUnits / totalFamilyUnits }
}

// ---------------------------------------------------------------------------
// View
// ---------------------------------------------------------------------------

interface BundlePerformanceViewProps {
  integrationId: string
  monthStartUtc: string
  monthEndUtc: string
  minimumOrderTotal: number
  /** Kept for parent prop compat; period is fully derived from monthStartUtc/End. */
  timezone: string
  economicsSettings: EconomicsSettings
}

export function BundlePerformanceView({
  integrationId,
  monthStartUtc,
  monthEndUtc,
  minimumOrderTotal,
  timezone,
  economicsSettings,
}: BundlePerformanceViewProps) {
  const tUi = useTranslations("Shopify.ShopifyEconomicsBundlePerformanceView")

  // Toolbar toggles live in URL state (`?show_archived`, `?show_hypothetical`)
  // so reloads + shared links preserve them. Param absent → off; toggle off →
  // delete the param (keeps URLs short for the common case).
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const showArchived = searchParams.get("show_archived") === "true"
  const showHypothetical = searchParams.get("show_hypothetical") === "true"
  const setToggleParam = useCallback(
    (key: "show_archived" | "show_hypothetical", on: boolean) => {
      const next = new URLSearchParams(searchParams.toString())
      if (on) next.set(key, "true")
      else next.delete(key)
      const qs = next.toString()
      router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false })
    },
    [router, pathname, searchParams],
  )
  const setShowArchived = useCallback(
    (on: boolean) => setToggleParam("show_archived", on),
    [setToggleParam],
  )
  const setShowHypothetical = useCallback(
    (on: boolean) => setToggleParam("show_hypothetical", on),
    [setToggleParam],
  )

  const { bundleTypes, hypotheticalTypes, totals, isLoading, error } = useBundlePerformance({
    integrationId,
    periodStart: monthStartUtc,
    periodEnd: monthEndUtc,
    minimumOrderTotal,
    freeShippingThreshold: economicsSettings.free_shipping_threshold,
    timezone,
    includeHypothetical: showHypothetical,
  })

  // Drill-down sheet — null = closed. We key by bundle_definition_id (UNIQUE per
  // integration in the response, across BOTH real and hypothetical rows), so
  // swapping rows mid-open is straightforward. `tab` lets cell-level clicks
  // route the user to the matching view: Bundle-cells → Instances tab,
  // Standalone-cells → Standalone tab, plain row click → Top SKUs (default).
  const [selectedDrillDown, setSelectedDrillDown] = useState<{
    bundleDefinitionId: string
    tab: DrillDownTab
  } | null>(null)
  const openDrillDown = useCallback(
    (bundleDefinitionId: string, tab: DrillDownTab = "skus") => {
      setSelectedDrillDown({ bundleDefinitionId, tab })
    },
    [],
  )

  const visibleTypes = useMemo(
    () => bundleTypes.filter((t) => showArchived || t.ends_at == null),
    [bundleTypes, showArchived],
  )

  const computedRows = useMemo(
    () =>
      visibleTypes.map((type) => ({
        type,
        computed: computeBundleTypePerformance(type, economicsSettings),
      })),
    [visibleTypes, economicsSettings],
  )

  const hypotheticalComputedRows = useMemo(
    () =>
      hypotheticalTypes.map((type) => ({
        type,
        computed: computeBundleTypePerformance(type, economicsSettings),
      })),
    [hypotheticalTypes, economicsSettings],
  )

  const hasArchived = useMemo(() => bundleTypes.some((t) => t.ends_at != null), [bundleTypes])

  // Drill-down lookup spans both real and hypothetical — definition IDs are
  // globally unique, so a single .find() across the concatenation finds the
  // right row regardless of which section was clicked.
  const selectedType = useMemo(() => {
    if (selectedDrillDown == null) return null
    const id = selectedDrillDown.bundleDefinitionId
    return (
      bundleTypes.find((t) => t.bundle_definition_id === id) ??
      hypotheticalTypes.find((t) => t.bundle_definition_id === id) ??
      null
    )
  }, [selectedDrillDown, bundleTypes, hypotheticalTypes])

  return (
    <TooltipProvider delayDuration={300}>
      <div className="space-y-4">
        <Toolbar
          showArchived={showArchived}
          onShowArchivedChange={setShowArchived}
          archivedAvailable={hasArchived}
          showHypothetical={showHypothetical}
          onShowHypotheticalChange={setShowHypothetical}
        />

        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCardSkeleton />
            <KpiCardSkeleton />
            <KpiCardSkeleton />
            <KpiCardSkeleton />
          </div>
        ) : !error &&
          bundleTypes.length === 0 &&
          (!showHypothetical || hypotheticalTypes.length === 0) ? (
          // Nothing real saved AND (hypothetical toggle off OR no hypothetical
          // rows either). Show the onboarding empty state. A tenant with ONLY
          // hypothetical definitions falls through to the section below so
          // their what-if data stays reachable.
          <EmptyState />
        ) : !error && totals != null ? (
          <>
            {bundleTypes.length > 0 && (
              <Kpis totalsRevenue={totals.total_period_revenue} bundleRevenue={totals.total_bundle_revenue} bundleOrderCount={totals.total_bundle_orders} typeCount={totals.bundle_types_count} />
            )}

            {bundleTypes.length > 0 &&
              (computedRows.length === 0 ? (
                // Bundle types exist but all are archived and toggle is off.
                <p className="text-sm text-muted-foreground italic">
                  <span key={tUi("allBundleTypesAreArchivedToggleShowArchived")}>{tUi("allBundleTypesAreArchivedToggleShowArchived")}</span></p>
              ) : (
                <BundleTypesTable
                  rows={computedRows}
                  periodStart={monthStartUtc}
                  periodEnd={monthEndUtc}
                  timezone={timezone}
                  onCellClick={openDrillDown}
                />
              ))}

            {showHypothetical && (
              <HypotheticalSection
                rows={hypotheticalComputedRows}
                periodStart={monthStartUtc}
                periodEnd={monthEndUtc}
                timezone={timezone}
                onCellClick={openDrillDown}
              />
            )}
          </>
        ) : null}

        {selectedType && (
          <BundleTypeDrillDown
            integrationId={integrationId}
            type={selectedType}
            periodStart={monthStartUtc}
            periodEnd={monthEndUtc}
            minimumOrderTotal={minimumOrderTotal}
            timezone={timezone}
            open
            initialTab={selectedDrillDown?.tab ?? "skus"}
            onOpenChange={(o) => {
              if (!o) setSelectedDrillDown(null)
            }}
          />
        )}
      </div>
    </TooltipProvider>
  )
}

// ---------------------------------------------------------------------------
// Toolbar
// ---------------------------------------------------------------------------

interface ToolbarProps {
  showArchived: boolean
  onShowArchivedChange: (v: boolean) => void
  archivedAvailable: boolean
  showHypothetical: boolean
  onShowHypotheticalChange: (v: boolean) => void
}

function Toolbar({
  showArchived,
  onShowArchivedChange,
  archivedAvailable,
  showHypothetical,
  onShowHypotheticalChange,
}: ToolbarProps) {
  const tUi = useTranslations("Shopify.ShopifyEconomicsBundlePerformanceView")

  return (
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <div className="text-sm text-muted-foreground">
        <span><span key={tUi("bundleTypesComeFromBundleDefinitionsManageThem")}>{tUi("bundleTypesComeFromBundleDefinitionsManageThem")}</span></span>{" "}
        <span className="font-medium"><span key={tUi("bundlesFamilies")}>{tUi("bundlesFamilies")}</span></span> <span><span key={tUi("page")}>{tUi("page")}</span></span>
      </div>
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <Switch checked={showHypothetical} onCheckedChange={onShowHypotheticalChange} />
          <span><span key={tUi("showWhatIfBundles")}>{tUi("showWhatIfBundles")}</span></span>
        </label>
        {archivedAvailable && (
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <Switch checked={showArchived} onCheckedChange={onShowArchivedChange} />
            <span><span key={tUi("showArchived")}>{tUi("showArchived")}</span></span>
          </label>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// KPI cards
// ---------------------------------------------------------------------------

function Kpis({
  totalsRevenue,
  bundleRevenue,
  bundleOrderCount,
  typeCount,
}: {
  totalsRevenue: number
  bundleRevenue: number
  bundleOrderCount: number
  typeCount: number
}) {
  const tUi = useTranslations("Shopify.ShopifyEconomicsBundlePerformanceView")

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <KpiCard
        label={tUi("totalRevenue")}
        icon={DollarSign}
        formattedValue={fmtCurrency.format(totalsRevenue)}
        subtitle={tUi("allOrdersInPeriodBundleStandalone")}
      />
      <KpiCard
        label={tUi("bundleRevenue")}
        icon={TrendingUp}
        formattedValue={fmtCurrency.format(bundleRevenue)}
        subtitle={tUi("fromBundleLineItemsOnly")}
      />
      <KpiCard
        label={tUi("bundleOrders")}
        icon={ArrowUpRight}
        formattedValue={fmtInt.format(bundleOrderCount)}
        subtitle={tUi("distinctOrdersContainingAnyBundle")}
      />
      <KpiCard
        label={tUi("bundleTypes")}
        icon={Package}
        formattedValue={fmtInt.format(typeCount)}
        subtitle={tUi("definedBundlesWithActivity")}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyState() {
  const tUi = useTranslations("Shopify.ShopifyEconomicsBundlePerformanceView")

  return (
    <div className="flex flex-col items-center justify-center h-48 text-muted-foreground gap-2">
      <Package className="h-8 w-8" />
      <p className="text-sm font-medium"><span key={tUi("noBundleTypesDetectedForThisPeriod")}>{tUi("noBundleTypesDetectedForThisPeriod")}</span></p>
      <p className="text-xs max-w-md text-center">
        <span key={tUi("makeSureBundleDefinitionsAreSetUpAnd")}>{tUi("makeSureBundleDefinitionsAreSetUpAnd")}</span></p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

interface ComputedRow {
  type: BundleTypePerformance
  computed: ComputedBundleTypePerformance
}

/**
 * Build a "shared standalone profit pool" lookup. The orchestrator's
 * splitStandaloneByFamilyKeys assigns one solo line_item to every bundle type
 * whose families overlap, so the same dollar value can repeat across rows
 * (e.g. all `bags:N|holsters:N` types share standalone bag+holster activity).
 *
 * We bucket rows by their rounded standalone profit so we can mark repeats
 * visually without re-deriving family overlap on the client. Bucket size > 1
 * means "this exact standalone-profit dollar appears in N rows — they share a
 * pool". Returned set contains the rounded values that should be flagged.
 */
function detectSharedStandaloneProfits(rows: ComputedRow[]): Set<number> {
  const counts = new Map<number, number>()
  for (const r of rows) {
    // Round to cents — floating-point sums of identical inputs occasionally
    // diverge by 1e-13, which would split a real shared bucket.
    const key = Math.round(r.computed.standaloneComputed.net_profit * 100)
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  const shared = new Set<number>()
  for (const [key, n] of counts) {
    if (n > 1) shared.add(key)
  }
  return shared
}

const STANDALONE_PROFIT_SHARED_TOOLTIP =
  "Same standalone profit appears in multiple rows because they share family components. " +
  "Don't sum across rows — the same solo sales are attributed to each bundle type whose families they cover."

function BundleTypesTable({
  rows,
  periodStart,
  periodEnd,
  timezone,
  onCellClick,
}: {
  rows: ComputedRow[]
  /** UI analysis period — drives D4 dimming for rows whose lifetime doesn't intersect. */
  periodStart: string
  periodEnd: string
  /** User's IANA tz — needed to interpret starts_at/ends_at calendar literals. */
  timezone: string
  /**
   * Per-cell click handler. `tab` follows the cell's column group:
   *   - `data-cell-group="bundle"`  → `tab="instances"` (decompose this
   *     bundle's instances)
   *   - `data-cell-group="standalone"` → `tab="standalone"` (per-family
   *     standalone breakdown)
   *   - anything else (identity column, baseline columns) → `tab="skus"`
   *     (top SKUs — the legacy row-click landing)
   */
  onCellClick: (bundleDefinitionId: string, tab: DrillDownTab) => void
}) {
  const tUi = useTranslations("Shopify.ShopifyEconomicsBundlePerformanceView")

  const sharedStandaloneKeys = useMemo(() => detectSharedStandaloneProfits(rows), [rows])

  return (
    <div className="rounded-md border overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          {/* Three colspan groups so users instantly see which columns are
              UI-period-scoped vs lifetime-scoped. The ordering below has
              been chosen so each group's columns are contiguous. */}
          <tr className="border-b bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
            <th className="sticky left-0 bg-muted/30 px-3 py-1" style={STICKY_NAME_COL} colSpan={2}>
              <span className="sr-only"><span key={tUi("identity")}>{tUi("identity")}</span></span>
            </th>
            <th className="text-center px-3 py-1 border-l" colSpan={14}>
              <span key={tUi("bundleCurrentPeriod")}>{tUi("bundleCurrentPeriod")}</span></th>
            <th className="text-center px-3 py-1 border-l" colSpan={5}>
              <span key={tUi("familySnapshotCurrentPeriod")}>{tUi("familySnapshotCurrentPeriod")}</span></th>
            <th className="text-center px-3 py-1 border-l" colSpan={5}>
              <span key={tUi("vsPreLaunchBaselineLifetime")}>{tUi("vsPreLaunchBaselineLifetime")}</span></th>
          </tr>
          <tr className="border-b bg-muted/50">
            <ThHeader sticky label={tUi("bundle")} tooltip={tUi("bundleDefinitionName")} style={STICKY_NAME_COL} />
            <ThHeader label={tUi("launch")} tooltip={tUi("launchDateSetOnTheBundleDefinitionAnchors")} />
            {/* Bundle (current period) — 14 cols */}
            <ThHeader label={tUi("orders")} tooltip={tUi("distinctOrdersContainingThisBundleTypeCurrentPeriod")} />
            <ThHeader label={tUi("revenue")} tooltip={tUi("revenueFromBundleLineItemsCurrentPeriod")} />
            <ThHeader label={tUi("discounts")} tooltip={tUi("sumOfDiscountAllocationsOnBundleLineItems")} />
            <ThHeader label={tUi("cOGS")} tooltip={tUi("costOfGoodsSoldForBundleLineItems")} />
            <ThHeader label={tUi("recipeCOGS")} tooltip={tUi("recipeCogsTooltip")} />
            <ThHeader label={tUi("cAC")} tooltip={tUi("customerAcquisitionCostBundleOrdersBlendedCacCurrent")} />
            <ThHeader label={tUi("shipping")} tooltip={tUi("brandShippingCostForBundleOrdersCurrentPeriod")} />
            <ThHeader label={tUi("shopifyFee")} tooltip={tUi("paymentProcessingFeeAllocatedToBundleLineItems")} />
            <ThHeader label={tUi("return")} tooltip={tUi("returnRateOfBundleLineItemsCurrentPeriod")} />
            <ThHeader label={tUi("refundCost")} tooltip={tUi("refundedAmountOnBundleLineItemsCurrentPeriod")} />
            <ThHeader label={tUi("returned")} tooltip={tUi("unitsReturnedFromBundleLineItemsCurrentPeriod")} />
            <ThHeader label={tUi("labelsCost")} tooltip={tUi("returnLabelCostUnitsReturnedReturnLabelCost")} />
            <ThHeader label={tUi("bundleProfit")} tooltip={tUi("netProfitFromBundleLineItemsOnlyThis")} />
            <ThHeader label={tUi("profitOrder")} tooltip={tUi("bundleNetProfitBundleOrdersCurrentPeriod")} />
            {/* Family snapshot (current period) — 5 cols */}
            <ThHeader
              label={tUi("standaloneProfit")}
              tooltip={"Net profit from standalone line_items of this type's families (current period)." + STANDALONE_OVERLAP_NOTE}
            />
            <ThHeader
              label={tUi("standalone")}
              tooltip={"Units of this type's families sold standalone (not in any bundle) in current period." + STANDALONE_OVERLAP_NOTE}
            />
            <ThHeader label={tUi("bundle")} tooltip={tUi("unitsSoldInsideBundlesOfThisTypeIn")} />
            <ThHeader label={tUi("total")} tooltip={tUi("bundleStandaloneUnitsForThisTypeCurrentPeriod")} />
            <ThHeader label={tUi("bundleShare")} tooltip={BUNDLE_SHARE_TOOLTIP} />
            {/* Vs pre-launch baseline (lifetime) — 5 cols */}
            <ThHeader
              label={tUi("preLaunchBaselineLifetime")}
              tooltip={"Units sold by this type's families during the lifetime baseline window — same length as the lifetime current window, ending at starts_at." + LIFETIME_WINDOW_NOTE}
            />
            <ThHeader
              label={tUi("netUnitLiftLifetime")}
              tooltip={"Lifetime current units − lifetime baseline units. Apples-to-apples since both windows have identical length." + LIFETIME_WINDOW_NOTE + INCREMENTAL_SEASONALITY_NOTE}
            />
            <ThHeader label={tUi("lifetimeCannibalization")} tooltip={TRUE_CANNIBALIZATION_TOOLTIP} />
            <ThHeader
              label={tUi("preLaunchProfitLifetime")}
              tooltip={"Net profit of this type's families during the lifetime baseline window (before launch)." + LIFETIME_WINDOW_NOTE}
            />
            <ThHeader
              label={tUi("lifetimeProfitLift")}
              tooltip={"Lifetime current profit − lifetime baseline profit. Independent of the period selector above." + LIFETIME_WINDOW_NOTE}
            />
          </tr>
        </thead>
        <tbody>
          {rows.map(({ type, computed }) => {
            const standaloneKey = Math.round(computed.standaloneComputed.net_profit * 100)
            const standaloneShared = sharedStandaloneKeys.has(standaloneKey)
            return (
              <BundleTypeRow
                key={type.bundle_definition_id}
                type={type}
                computed={computed}
                standaloneShared={standaloneShared}
                lifetimeIntersectsPeriod={lifetimeIntersectsPeriod(type, periodStart, periodEnd, timezone)}
                onCellClick={(tab) => onCellClick(type.bundle_definition_id, tab)}
              />
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Hypothetical bundles section
// ---------------------------------------------------------------------------

// Wraps a second BundleTypesTable for `hypothetical_types[]`. The rows are
// computed by the same orchestrator path as real types — hypothetical
// definitions run against the full original line items, never drawing from
// or competing with the real consumption pool, so `bundle_types` and
// `totals` are byte-identical with the flag on vs off. We surface the
// section with explicit «what-if» framing so users never confuse the
// predictions with realised data — heading + leading copy + visual frame.
function HypotheticalSection({
  rows,
  periodStart,
  periodEnd,
  timezone,
  onCellClick,
}: {
  rows: ComputedRow[]
  periodStart: string
  periodEnd: string
  timezone: string
  onCellClick: (bundleDefinitionId: string, tab: DrillDownTab) => void
}) {
  const tUi = useTranslations("Shopify.ShopifyEconomicsBundlePerformanceView")

  return (
    <section className="space-y-2 rounded-md border border-dashed bg-muted/30 p-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold"><span key={tUi("whatIfBundles")}>{tUi("whatIfBundles")}</span></h2>
          <p className="text-xs text-muted-foreground">
            <span key={tUi("predictionsForHypotheticalBundleDefinitionsAggregatedInIsolation")}>{tUi("predictionsForHypotheticalBundleDefinitionsAggregatedInIsolation")}</span></p>
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm italic text-muted-foreground">
          <span key={tUi("noHypotheticalBundleDefinitionsSavedYetMarkA")}>{tUi("noHypotheticalBundleDefinitionsSavedYetMarkA")}</span></p>
      ) : (
        <BundleTypesTable
          rows={rows}
          periodStart={periodStart}
          periodEnd={periodEnd}
          timezone={timezone}
          onCellClick={onCellClick}
        />
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

/** Recipe COGS: em-dash without SKU components, a warning badge while incomplete. */
function RecipeCogsCell({
  recipe,
}: {
  recipe: BundleTypePerformance["recipe_cogs"]
}) {
  const recipeWarning = useRecipeCogsWarning()
  if (recipe === undefined) return <>—</>
  const warning = recipeWarning(recipe.status)
  if (warning == null) {
    return <>{fmtCurrency.format(recipe.value)}</>
  }
  return (
    <Tooltip>
      <TooltipTrigger
        className="inline-flex items-center gap-1"
        data-testid="recipe-cogs-status"
        data-status={recipe.status}
      >
        <span>{fmtCurrency.format(recipe.value)}</span>
        <Info className="h-3 w-3 text-amber-600" />
      </TooltipTrigger>
      <TooltipContent className="max-w-[320px]">
        {warning}
      </TooltipContent>
    </Tooltip>
  )
}

const BundleTypeRow = memo(function BundleTypeRow({
  type,
  computed,
  standaloneShared,
  lifetimeIntersectsPeriod: lifetimeIntersects,
  onCellClick,
}: {
  type: BundleTypePerformance
  computed: ComputedBundleTypePerformance
  /** True when this row's standalone profit dollar value is shared with at least one other row (overlapping families). */
  standaloneShared: boolean
  /**
   * False when starts_at > period_end OR ends_at < period_start.
   * Triggers row dimming + an inline indicator explaining "Group 1 zero by
   * design; Group 2 still valid". Rows that DO intersect render normally.
   */
  lifetimeIntersectsPeriod: boolean
  /**
   * Dispatched with the tab matching the clicked cell's column group.
   * Cells carry `data-cell-group="bundle"` / `"standalone"` so a single
   * row-level handler can route per-cell via event delegation without
   * threading per-cell handlers down the row.
   */
  onCellClick: (tab: DrillDownTab) => void
}) {

  const tUi = useTranslations("Shopify.ShopifyEconomicsBundlePerformanceView")

  // Note: lifetimeCurrentComputed (NOT currentAllComputed) is the LHS of the
  // Profit Lift threshold check — comparison is lifetime-scoped to keep it
  // apples-to-apples with `baselineComputed`.
  const { bundleComputed, standaloneComputed, baselineComputed, profitLift } = computed
  const baselineMissing = baselineComputed == null
  const baselineTooltip =
    type.baseline_status !== "ok" ? BASELINE_STATUS_TOOLTIP[type.baseline_status] : ""
  const standaloneProfit = standaloneComputed.net_profit
  const isArchived = type.ends_at != null

  // Single row-level click handler — derives the destination tab from the
  // clicked cell's `data-cell-group`. Identity / baseline cells (no group)
  // fall through to «skus», matching the row-as-default-trigger contract.
  const dispatchClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
    const cell = (e.target as HTMLElement).closest<HTMLTableCellElement>("td")
    const group = cell?.dataset.cellGroup
    if (group === "bundle") onCellClick("instances")
    else if (group === "standalone") onCellClick("standalone")
    else onCellClick("skus")
  }
  // Row is the drill-down trigger. role="button" + keyboard handler keep it
  // accessible. Space/Enter activate, scroll suppressed.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      onCellClick("skus")
    }
  }

  return (
    <tr
      className={cn(
        "border-b hover:bg-muted/30 cursor-pointer focus:outline-none focus:bg-muted/40",
        // D4: dim when bundle's lifetime doesn't intersect the UI period.
        // Stronger dim wins over the older `isArchived`-only path so deprecated
        // bundles outside the period stay legible (≈0.55 < 0.6).
        !lifetimeIntersects ? "opacity-55" : isArchived && "opacity-60",
      )}
      role="button"
      tabIndex={0}
      onClick={dispatchClick}
      onKeyDown={handleKeyDown}
      aria-label={tUi("viewBreakdownForValue0", { value0: String(type.display_label) })}
    >
      <td className="sticky left-0 bg-background px-3 py-2 align-top" style={STICKY_NAME_COL}>
        <div style={STICKY_NAME_COL_INNER_MAX}>
          <div className="font-medium break-words flex items-center gap-1">
            {type.display_label}
            {!lifetimeIntersects && <OutsidePeriodIcon />}
          </div>
          <div className="text-xs text-muted-foreground break-words">{type.bundle_definition_id}</div>
          {type.ends_at && (
            <div className="text-xs text-muted-foreground italic">
              <span><span key={tUi("archived")}>{tUi("archived")}</span></span> {formatYmdLiteral(type.ends_at)}
            </div>
          )}
        </div>
      </td>
      <td className="px-3 py-2 tabular-nums whitespace-nowrap text-muted-foreground">
        {formatYmdLiteral(type.starts_at)}
      </td>
      {/* Bundle (current period) — 14 cols, all tagged data-cell-group="bundle" so
          clicks anywhere in this band route to the Instances tab. */}
      <td data-cell-group="bundle" className="text-right px-3 py-2 tabular-nums">{fmtInt.format(type.bundle.order_count)}</td>
      <td data-cell-group="bundle" className="text-right px-3 py-2 tabular-nums">{fmtCurrency.format(type.bundle.total_revenue)}</td>
      <td data-cell-group="bundle" className="text-right px-3 py-2 tabular-nums">{fmtCurrency.format(type.bundle.total_discount)}</td>
      <td data-cell-group="bundle" className="text-right px-3 py-2 tabular-nums">
        {bundleComputed.effective_cogs != null ? fmtCurrency.format(bundleComputed.effective_cogs) : "—"}
      </td>
      <td data-cell-group="bundle" className="text-right px-3 py-2 tabular-nums">
        <RecipeCogsCell recipe={type.recipe_cogs} />
      </td>
      <td data-cell-group="bundle" className="text-right px-3 py-2 tabular-nums">{fmtCurrency.format(bundleComputed.total_cac)}</td>
      <td data-cell-group="bundle" className="text-right px-3 py-2 tabular-nums">{fmtCurrency.format(bundleComputed.brand_shipping)}</td>
      <td data-cell-group="bundle" className="text-right px-3 py-2 tabular-nums">{fmtCurrency.format(bundleComputed.shopify_fee)}</td>
      <td data-cell-group="bundle" className="text-right px-3 py-2 tabular-nums">{fmtPct.format(bundleComputed.return_rate)}</td>
      <td data-cell-group="bundle" className="text-right px-3 py-2 tabular-nums">{fmtCurrency.format(bundleComputed.refund_cost)}</td>
      <td data-cell-group="bundle" className="text-right px-3 py-2 tabular-nums">{fmtInt.format(type.bundle.units_returned)}</td>
      <td data-cell-group="bundle" className="text-right px-3 py-2 tabular-nums">
        {fmtCurrency.format(bundleComputed.return_labels_cost)}
      </td>
      <td
        data-cell-group="bundle"
        className={cn(
          "text-right px-3 py-2 tabular-nums font-medium",
          bundleComputed.net_profit >= 0 ? "text-green-600" : "text-red-600",
        )}
      >
        {fmtCurrency.format(bundleComputed.net_profit)}
      </td>
      <td data-cell-group="bundle" className="text-right px-3 py-2 tabular-nums">
        {fmtCurrency.format(bundleComputed.profit_per_order)}
      </td>
      {/* Family snapshot (current period) — 5 cols, all tagged data-cell-group="standalone"
          so clicks anywhere in this band route to the Standalone tab. */}
      <td
        data-cell-group="standalone"
        className={cn(
          "text-right px-3 py-2 tabular-nums border-l",
          standaloneProfit >= 0 ? "text-green-600" : "text-red-600",
          standaloneShared && "italic opacity-80",
        )}
      >
        {standaloneShared ? (
          <Tooltip>
            <TooltipTrigger className="inline-flex items-center gap-1">
              {fmtCurrency.format(standaloneProfit)}
              <Info className="h-3 w-3 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent className="max-w-[320px]">
              {STANDALONE_PROFIT_SHARED_TOOLTIP}
            </TooltipContent>
          </Tooltip>
        ) : (
          fmtCurrency.format(standaloneProfit)
        )}
      </td>
      <td data-cell-group="standalone" className="text-right px-3 py-2 tabular-nums">{fmtInt.format(type.standalone.units_sold)}</td>
      <td data-cell-group="standalone" className="text-right px-3 py-2 tabular-nums">{fmtInt.format(type.bundle.units_sold)}</td>
      <td data-cell-group="standalone" className="text-right px-3 py-2 tabular-nums">{fmtInt.format(type.current_all.units_sold)}</td>
      <td data-cell-group="standalone" className="text-right px-3 py-2 tabular-nums">
        <BundleShareCell type={type} />
      </td>
      {/* Vs pre-launch baseline (lifetime) — 5 cols, all baseline-status gated. */}
      <td className="text-right px-3 py-2 tabular-nums border-l">
        {baselineMissing || type.baseline == null ? (
          <BaselineMissingCell tooltip={baselineTooltip} />
        ) : (
          <BaselineUnitsCell type={type} units={type.baseline.units_sold} />
        )}
      </td>
      <td className="text-right px-3 py-2 tabular-nums">
        {baselineMissing || type.baseline == null || type.lifetime_current == null ? (
          <BaselineMissingCell tooltip={baselineTooltip} />
        ) : (
          // Net unit lift = lifetime_current.units − baseline.units.
          // Both windows have identical length, so the delta is apples-to-apples.
          fmtInt.format(type.lifetime_current.units_sold - type.baseline.units_sold)
        )}
      </td>
      <td className="text-right px-3 py-2 tabular-nums">
        <TrueCannibalCell type={type} baselineMissing={baselineMissing} baselineTooltip={baselineTooltip} />
      </td>
      <td className="text-right px-3 py-2 tabular-nums">
        {baselineMissing ? (
          <BaselineMissingCell tooltip={baselineTooltip} />
        ) : (
          fmtCurrency.format(baselineComputed!.net_profit)
        )}
      </td>
      <td
        className={cn(
          "text-right px-3 py-2 tabular-nums",
          baselineMissing || profitLift == null
            ? ""
            : profitLiftColorClass(profitLift, type.lifetime_current?.total_revenue ?? 0),
        )}
      >
        {baselineMissing || profitLift == null ? (
          <BaselineMissingCell tooltip={baselineTooltip} />
        ) : (
          fmtCurrency.format(profitLift)
        )}
      </td>
    </tr>
  )
})

function OutsidePeriodIcon() {
  const tUi = useTranslations("Shopify.ShopifyEconomicsBundlePerformanceView")

  return (
    <Tooltip>
      <TooltipTrigger className="inline-flex items-center text-muted-foreground" aria-label={tUi("outsideSelectedPeriod")}>
        <Info className="h-3 w-3" />
      </TooltipTrigger>
      <TooltipContent className="max-w-[320px]">
        <span key={tUi("outsideSelectedPeriodCurrentPeriodColumnsAreZero")}>{tUi("outsideSelectedPeriodCurrentPeriodColumnsAreZero")}</span></TooltipContent>
    </Tooltip>
  )
}

function BaselineUnitsCell({ type, units }: { type: BundleTypePerformance; units: number }) {
  const tUi = useTranslations("Shopify.ShopifyEconomicsBundlePerformanceView")

  // D10 truncated indicator: append (*) when the lifetime window had to shrink
  // below 12 months because pre-launch history was short. Effective window
  // length derived from baseline_window dates.
  if (!type.baseline_truncated || type.baseline_window == null) {
    return <span>{fmtInt.format(units)}</span>
  }
  const startMs = Date.parse(type.baseline_window.start)
  const endMs = Date.parse(type.baseline_window.end)
  const days =
    Number.isFinite(startMs) && Number.isFinite(endMs)
      ? Math.round((endMs - startMs) / (24 * 60 * 60 * 1000))
      : null
  const tooltip =
    days != null
      ? `Effective window: ${days} days — limited by integration history.`
      : BASELINE_TRUNCATED_TOOLTIP_BASE
  return (
    <Tooltip>
      <TooltipTrigger className="inline-flex items-center gap-0.5">
        <span>{fmtInt.format(units)}</span>
        <span className="text-amber-600 font-medium" aria-label={tUi("truncatedBaseline")}>(*)</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-[320px]">{tooltip}</TooltipContent>
    </Tooltip>
  )
}

function BaselineMissingCell({ tooltip }: { tooltip: string }) {
  if (!tooltip) return <span className="text-muted-foreground">—</span>
  return (
    <Tooltip>
      <TooltipTrigger className="inline-flex items-center gap-1 text-muted-foreground">
        <span>—</span> <Info className="h-3 w-3" />
      </TooltipTrigger>
      <TooltipContent className="max-w-[280px]">{tooltip}</TooltipContent>
    </Tooltip>
  )
}

function BundleShareCell({ type }: { type: BundleTypePerformance }) {
  const tUi = useTranslations("Shopify.ShopifyEconomicsBundlePerformanceView")

  const decision = bundleShareDisplay(type, tUi)
  if (!decision.ok) {
    return (
      <Tooltip>
        <TooltipTrigger className="inline-flex items-center gap-1 text-muted-foreground">
          <span>—</span> <Info className="h-3 w-3" />
        </TooltipTrigger>
        <TooltipContent className="max-w-[320px]">{decision.tooltip}</TooltipContent>
      </Tooltip>
    )
  }
  return (
    <Tooltip>
      <TooltipTrigger>
        <span>{fmtPct.format(decision.value)}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-[320px]">{BUNDLE_SHARE_TOOLTIP}</TooltipContent>
    </Tooltip>
  )
}

// True Cannibalization is only meaningful with a valid baseline. When baseline
// is missing, fall through to the same '—' + reason shown for the other
// Group-2 metrics (Baseline / Incremental / Profit Lift) — keeps tooltip-text
// consistent across baseline-dependent columns.
//
// Coloring: high cannibalization is bad (bundle absorbed solo demand without
// growing the pie), so >50% gets red. 0-20% is healthy (bundle attracted
// mostly new buyers); industry benchmark per research (GrowthFactor 2026):
// 0-10% ideal, 10-20% moderate, 20-30% high, 30%+ concerning.
function trueCannibalColorClass(pct: number): string {
  if (pct >= 0.5) return "text-red-600"
  if (pct >= 0.2) return "text-amber-600"
  return "text-muted-foreground"
}

function TrueCannibalCell({
  type,
  baselineMissing,
  baselineTooltip,
}: {
  type: BundleTypePerformance
  baselineMissing: boolean
  baselineTooltip: string
}) {
  if (baselineMissing || type.true_cannibalization_pct == null) {
    return <BaselineMissingCell tooltip={baselineTooltip} />
  }
  return (
    <Tooltip>
      <TooltipTrigger>
        <span className={trueCannibalColorClass(type.true_cannibalization_pct)}>
          {fmtPct.format(type.true_cannibalization_pct)}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-[320px]">{TRUE_CANNIBALIZATION_TOOLTIP}</TooltipContent>
    </Tooltip>
  )
}
